import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { getColors, ColorScheme } from '../utils/colorScheme';
import { mergeUiConceptIntoScheme } from '../concept/conceptDesignRegistry';
import { ConceptExperienceContext } from '../concept/ConceptExperienceContext';
import { syncLiveApplicationsFromColorScheme, clearLiveApplicationsOverride } from '../utils/mmecBrandColors';
import { useMMECTheme } from './MMECThemeContext';
import { readMmecTokensIntoColorScheme } from '../theme/mmecPaletteIntoColorScheme';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  colors: ColorScheme;
  setTheme: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export { ThemeContext };

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const conceptCtx = useContext(ConceptExperienceContext);
  const conceptId = conceptCtx?.conceptId ?? 'legacy';
  const { palette } = useMMECTheme();

  // Default to dark mode on platform load (MME Analytics Platform primary theme)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('mme-theme');
      // Default dark on platform load; only 'light' uses light theme
      return savedTheme === 'light' ? false : true;
    }
    return true;
  });

  const colors = useMemo(() => {
    const base = getColors(isDarkMode);
    const merged = mergeUiConceptIntoScheme(conceptId, isDarkMode, base);
    return readMmecTokensIntoColorScheme(merged, palette);
  }, [conceptId, isDarkMode, palette]);

  // Apply theme synchronously on mount (before first render)
  if (typeof window !== 'undefined') {
    const root = document.documentElement;

    if (isDarkMode) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    if (root && root.style) {
      root.style.backgroundColor = colors.background;
    }
    if (document.body && document.body.style) {
      document.body.style.backgroundColor = colors.background;
    }
  }

  useEffect(() => {
    const root = document.documentElement;

    if (isDarkMode) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    if (root && root.style) {
      Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--mme-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
      });

      root.style.backgroundColor = colors.background;
    }
    if (document.body && document.body.style) {
      document.body.style.backgroundColor = colors.background;
    }

    if (conceptId === 'legacy') {
      clearLiveApplicationsOverride();
    } else {
      syncLiveApplicationsFromColorScheme(colors);
    }

    localStorage.setItem('mme-theme', isDarkMode ? 'dark' : 'light');
    return () => {
      clearLiveApplicationsOverride();
    };
  }, [isDarkMode, colors, conceptId]);


  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const setTheme = (isDark: boolean) => {
    setIsDarkMode(isDark);
  };

  const value: ThemeContextType = {
    isDarkMode,
    toggleTheme,
    colors,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 